import { gql } from "@apollo/client";

export const FETCH_PREV_PLANS = gql`
  query FETCH_PREV_PLANS(
    $statesIn: [String]
    $options: CommonOptionsInput
    $rootPlans: Boolean
  ) {
    allProductionPlans(
      statesIn: $statesIn
      options: $options
      rootPlans: $rootPlans
    ) {
      results {
        id
        name
        wisCustomer {
          id
          name
          system
        }
        planType
        odooSalesOrderPaymentTermsId
        hasPrepaymentInPaymentTerm
        startDate
        readyDate
        state
        odooObject {
          objectType
          objectId
          objectName
        }
        createdBy {
          firstName
        }
        handledBy {
          firstName
        }
        nextActionUser {
          id
          firstName
        }
      }
      total
    }
  }
`;

const PRODUCTION_PLAN_LINE_FRAGMENT = gql`
  fragment productionPlanLine on ProductionPlanLineType {
    id
    name
    product {
      id
      number
      name
      odooId
      odooIdInc
      itemsPerSet
      quantityPerCarton
      cbm: outerCartonCbm
      ctnGrossWeight
      isActive
      considerBomInProduction
      version
      countryCodeOfOrigin
      cpscProduct {
        id
        versionId
      }
      complianceCertificates {
        id
        certificateType
      }
    }
    productOdooId
    qty
    reduceQty
    finalQty
    salesPrice
    discountPercentage
    totalPrice
    totalCartons
    totalGrossWeight
    totalCbm
    unitCost
    totalCost
    readyDate
  }
`;

const PRODUCTION_PLAN_FRAGMENT = gql`
  fragment productionPlan on ProductionPlanType {
    id
    planType
    latestPlanId
    parent {
      id
      name
      totalCbm
      children {
        id
        name
        totalCbm
      }
    }
    name
    wisCustomer {
      id
      name
      odooPartnerId
      system
      shippingZone
    }
    requiredCertificateTypes
    wisPriceList {
      id
      name
      currency
    }
    skipCustomerStages
    extraData

    customer
    data
    startDate
    readyDate
    finalReadyDate
    lines {
      ...productionPlanLine
    }
    planType
    orderType
    state
    odooObject {
      id
      objectId
      objectName
      objectType
    }
    currency
    totalQty
    productCost
    totalCbm
    totalCost
    totalPrice

    finalLines {
      ...productionPlanLine
    }
    finalTotalQty
    finalTotalGrossWeight
    finalTotalCbm
    finalTotalCost
    finalTotalPrice
    joinQueueAt
    salesConfirmAt
    customerConfirmAt
    confirmTimeAt
    costCompareValue
    requiredToConfirmCost
    confirmCostAt
    handleAt
    cancelAt
    odooTransferOperationTypeId
    odooTransferFromLocationId
    odooTransferToLocationId
    odooSalesOrderWarehouseId
    odooSalesOrderPriceListId
    odooSalesOrderPaymentTermsId
    hasPrepaymentInPaymentTerm
    odooTaxId
    odooSalesOrderClientOrderRef
    odooSalesOrderSourceId
    odooSoUserId
    odooIncoterm
    odooXDeliverymethod
    autoSendToOdoo
    description
    handledBy {
      id
      firstName
    }
    createdBy {
      id
      firstName
    }
    nextActionUser {
      id
      firstName
    }
    handleRemarks
  }
  ${PRODUCTION_PLAN_LINE_FRAGMENT}
`;

export const FETCH_ADDITIONAL_PLANS = gql`
  query FETCH_ADDITIONAL_PLANS($id: ID!) {
    productionPlan(id: $id) {
      id
      state
      requiredCertificateTypes
      children {
        ...productionPlan
      }
    }
  }
  ${PRODUCTION_PLAN_FRAGMENT}
`;

export const FETCH_PRODUCTION_PLAN = gql`
  query FETCH_PRODUCTION_PLAN($id: ID!) {
    productionPlan(id: $id) {
      ...productionPlan
      data
      children {
        id
      }
    }
  }
  ${PRODUCTION_PLAN_FRAGMENT}
`;

export const COMPUTE_PRODUCTION_PLAN = gql`
  mutation COMPUTE_PRODUCTION_PLAN($id: ID!) {
    computeProductionPlan(id: $id) {
      productionPlan {
        ...productionPlan
      }
    }
  }
  ${PRODUCTION_PLAN_FRAGMENT}
`;

export const RECOMPUTE_PRODUCTION_PLAN = gql`
  mutation ($planId: ID!, $sendNotification: Boolean) {
    recomputeProductionPlan(
      planId: $planId
      sendNotification: $sendNotification
    ) {
      plan {
        ...productionPlan
      }
    }
  }
  ${PRODUCTION_PLAN_FRAGMENT}
`;

export const CHANGE_PLAN_START_DATE = gql`
  mutation ($planId: ID!, $startDate: DateTime!) {
    changeProductionPlanStartDate(planId: $planId, startDate: $startDate) {
      plan {
        ...productionPlan
      }
    }
  }
  ${PRODUCTION_PLAN_FRAGMENT}
`;

export const CREATE_PRODUCTION_PLAN = gql`
  mutation CREATE_PRODUCTION_PLAN(
    $name: String!
    $planType: String!
    $wisCustomerId: ID
    $parentId: ID
    $orderType: String
    $startDate: DateTime
    $autoSendToOdoo: Boolean
    $lines: [CreateProductionPlanLineInputType]
  ) {
    createProductionPlan(
      name: $name
      planType: $planType
      wisCustomerId: $wisCustomerId
      parentId: $parentId
      orderType: $orderType
      startDate: $startDate
      autoSendToOdoo: $autoSendToOdoo
      lines: $lines
    ) {
      productionPlan {
        ...productionPlan
        parent {
          id
          ...productionPlan
          children {
            ...productionPlan
          }
        }
      }
    }
  }
  ${PRODUCTION_PLAN_FRAGMENT}
`;

export const SAVE_PRODUCTION_PLAN = gql`
  mutation SAVE_PRODUCTION_PLAN(
    $id: ID!
    $name: String
    $startDate: DateTime
    $wisCustomerId: ID
    $wisPriceListId: ID
    $currency: String
    $planType: String
    $orderType: String
    $odooTransferOperationTypeId: Int
    $odooTransferFromLocationId: Int
    $odooTransferToLocationId: Int
    $odooSalesOrderWarehouseId: Int
    $odooSalesOrderPriceListId: Int
    $odooSalesOrderPaymentTermsId: Int
    $odooTaxId: Int
    $odooSalesOrderClientOrderRef: String
    $odooIncoterm: Int
    $odooXDeliverymethod: Int
    $odooSalesOrderSourceId: Int
    $odooSoUserId: Int
    $hasPrepaymentInPaymentTerm: Boolean
    $requiredCertificateTypes: String
    $autoSendToOdoo: Boolean
    $description: String
    $extraData: String
  ) {
    saveProductionPlan(
      id: $id
      name: $name
      startDate: $startDate
      wisCustomerId: $wisCustomerId
      wisPriceListId: $wisPriceListId
      currency: $currency
      planType: $planType
      orderType: $orderType
      odooTransferOperationTypeId: $odooTransferOperationTypeId
      odooTransferFromLocationId: $odooTransferFromLocationId
      odooTransferToLocationId: $odooTransferToLocationId
      odooSalesOrderWarehouseId: $odooSalesOrderWarehouseId
      odooSalesOrderPriceListId: $odooSalesOrderPriceListId
      odooSalesOrderPaymentTermsId: $odooSalesOrderPaymentTermsId
      odooTaxId: $odooTaxId
      odooSalesOrderClientOrderRef: $odooSalesOrderClientOrderRef
      odooIncoterm: $odooIncoterm
      odooXDeliverymethod: $odooXDeliverymethod
      odooSalesOrderSourceId: $odooSalesOrderSourceId
      odooSoUserId: $odooSoUserId
      hasPrepaymentInPaymentTerm: $hasPrepaymentInPaymentTerm
      requiredCertificateTypes: $requiredCertificateTypes
      autoSendToOdoo: $autoSendToOdoo
      description: $description
      extraData: $extraData
    ) {
      productionPlan {
        ...productionPlan
      }
    }
  }
  ${PRODUCTION_PLAN_FRAGMENT}
`;

export const UPDATE_PRODUCTION_PLAN_STATE = gql`
  mutation UPDATE_PRODUCTION_PLAN_STATE($id: ID!, $action: String!) {
    updateProductionPlanState(id: $id, action: $action) {
      productionPlan {
        id
        state
        readyDate
        salesConfirmAt
        customerConfirmAt
        confirmTimeAt
        costCompareValue
        requiredToConfirmCost
        confirmCostAt
        handleAt
        handledBy {
          id
          firstName
        }
        cancelAt
        odooObject {
          id
          objectId
          objectName
          objectType
        }
        odooIncPo {
          id
          objectId
          objectName
        }
        nextActionUser {
          id
          firstName
        }
        parent {
          id
          finalTotalQty
          finalTotalGrossWeight
          finalTotalCbm
          finalTotalCost
          finalTotalPrice
          finalReadyDate
        }
      }
    }
  }
`;

export const HANDLE_PRODUCTION_PLAN = gql`
  mutation HANDLE_PRODUCTION_PLAN(
    $id: ID!
    $handledByUserId: ID
    $handleRemarks: String
  ) {
    updateProductionPlanState(
      id: $id
      action: "HANDLE"
      handledByUserId: $handledByUserId
      handleRemarks: $handleRemarks
    ) {
      productionPlan {
        id
        state
        handledBy {
          id
          email
        }
        handleRemarks
      }
    }
  }
`;

export const SAVE_PRODUCTION_PLAN_EXTRA_DATA = gql`
  mutation SAVE_PRODUCTION_PLAN_EXTRA_DATA($id: ID!, $extraData: String) {
    saveProductionPlan(id: $id, extraData: $extraData) {
      productionPlan {
        id
        extraData
      }
    }
  }
`;

export const START_PROCESSING_PRODUCTION_PLAN = gql`
  mutation START_PROCESSING_PRODUCTION_PLAN($id: ID!) {
    updateProductionPlanState(id: $id, action: "START_PROCESSING") {
      productionPlan {
        id
        state
      }
    }
  }
`;

export const DELETE_PRODUCTION_PLAN = gql`
  mutation DELETE_PRODUCTION_PLAN($id: ID!) {
    deleteProductionPlan(id: $id) {
      deleted
    }
  }
`;

export const FETCH_PRODUCT_PRODUCTION_INFO = gql`
  query FETCH_PRODUCT_PRODUCTION_INFO($id: ID!) {
    product(id: $id) {
      id
      considerBomInProduction
      production {
        id
        descendants {
          category {
            id
          }
        }
      }
      productionCategory {
        id
        name
        factory {
          id
          name
        }
      }
      itemsPerSet
      children {
        id
        product {
          id
          number
          name
          productionCategory {
            id
            name
            factory {
              id
              name
            }
          }
        }
        qty
      }
    }
  }
`;

export const FETCH_PLAN_SUMMARY = gql`
  query FETCH_PLAN_SUMMARY($id: ID!) {
    productionPlan(id: $id) {
      id
      planType
      wisPriceList {
        id
        currency
      }
      requiredCertificateTypes
      odooSalesOrderWarehouseId
      extraData
      finalLines {
        ...productionPlanLine
      }
      finalTotalQty
      finalTotalGrossWeight
      finalTotalCbm
      finalTotalCost
      finalTotalPrice
      finalReadyDate
    }
  }
  ${PRODUCTION_PLAN_LINE_FRAGMENT}
`;

export const VALIDATE_PRODUCTION_PLAN = gql`
  query VALIDATE_PRODUCTION_PLAN($planId: ID!) {
    validateProductionPlan(planId: $planId)
  }
`;

export const FETCH_DATA_FOR_SYNC_PRICE_BACK = gql`
  query FETCH_DATA_FOR_SYNC_PRICE_BACK($id: ID!) {
    priceList(id: $id) {
      id
      name
      currency
      latestPrices {
        id
        price
        product {
          id
          name
          number
        }
        discountPercentage
        state
      }
    }
  }
`;

export const FETCH_PRODUCTION_PLAN_LOGS = gql`
  query FETCH_PRODUCTION_PLAN_LOGS($id: ID!) {
    productionPlan(id: $id) {
      id
      logs {
        id
        user {
          id
          firstName
        }
        createdAt
        operation
        effectedByPlan {
          id
          name
        }
        startDate
        readyDate
      }
    }
  }
`;

export const FETCH_PLAN_META_DATA = gql`
  query FETCH_PLAN_META_DATA($planId: ID!) {
    productionPlan(id: $planId) {
      id
      wisPriceList {
        id
        name
        currency
        latestPrices {
          id
          product {
            id
          }
          finalPrice
          state
        }
      }

      parent {
        id
        lines {
          id
          product {
            id
          }
          salesPrice
        }
        children(
          statesIn: [
            "SALES_CONFIRMED"
            "AWAITING_CUSTOMER"
            "AWAITING_PRODUCTION"
            "PLANING"
            "HANDLED"
            "PROCESSING"
          ]
        ) {
          id
          lines {
            id
            product {
              id
            }
            salesPrice
          }
        }
      }
    }
  }
`;

export const FETCH_E_FILING_PRODUCT_IDS = gql`
  query FETCH_E_FILING_PRODUCT_IDS {
    eFilingProductIds
  }
`;

export const OS_SAVE_PLAN_LINE = gql`
  mutation OsSavePlanLine(
    $planId: ID!
    $productId: ID
    $lineId: ID
    $qty: Int
    $salesPrice: Float
    $discountPercentage: Float
  ) {
    osSavePlanLine(
      planId: $planId
      productId: $productId
      lineId: $lineId
      qty: $qty
      salesPrice: $salesPrice
      discountPercentage: $discountPercentage
    ) {
      planLine {
        ...productionPlanLine
        plan {
          id
          data
          readyDate
          totalCartons
          totalCbm
          totalCost
        }
      }
    }
  }
  ${PRODUCTION_PLAN_LINE_FRAGMENT}
`;

export const OS_DELETE_PLAN_LINE = gql`
  mutation OsDeletePlanLine($planId: ID!, $lineIds: [ID!]!) {
    osDeletePlanLine(planId: $planId, lineIds: $lineIds) {
      lineIds
      productionPlan {
        id
        data
        readyDate
        totalCbm
      }
    }
  }
`;

export const REDUCE_PLAN = gql`
  mutation REDUCE_PLAN($planId: ID!, $lines: [ProductionPlanLineInputType]!) {
    reducePlan(planId: $planId, lines: $lines) {
      productionPlan {
        id
        lines {
          ...productionPlanLine
        }
        totalQty
        totalCartons
        totalCbm
        totalCost
        totalPrice
        finalTotalQty
        finalTotalGrossWeight
        finalTotalCbm
        finalTotalCost
        finalTotalPrice
      }
    }
  }
  ${PRODUCTION_PLAN_LINE_FRAGMENT}
`;
